<template>
  <div>
    <div class="mb-1">
      <v-breadcrumbs
        class="px-0"
        :items="accountBreadcrumbItems"
      >
      </v-breadcrumbs>
    </div>
    <div v-if="child">
      <div class="mb-8 d-flex align-center">
        <h2>{{ child.lastName }} {{ child.firstName }}</h2>
        <v-spacer></v-spacer>
        <div>
          <v-btn
            color="primary"
            class="mr-4"
            variant="outlined"
            @click="invitationDownload"
            :disabled="isDownloading || loading"
          >
            <v-icon start>mdi-tray-arrow-down</v-icon>
            {{ $t('children.invitationDownload') }}
          </v-btn>
          <v-btn
            @click="deleteChild"
            :disabled="loading"
            class="mr-4"
            color="error"
            variant="flat"
          >
            {{ $t('common.delete') }}
          </v-btn>
          <v-btn
            :disabled="loading"
            color="primary"
            variant="flat"
            :to="`/children/account/${child.childId}/edit`"
            >{{ $t('common.edit') }}</v-btn
          >
        </div>
      </div>

      <div>
        <v-progress-linear
          v-if="isDownloading"
          indeterminate
          color="primary"
          class="my-8"
        ></v-progress-linear>
      </div>

      <div class="my-8">
        <v-img width="200px" :src="child.imageUrl"></v-img>
      </div>

      <h3>{{ $t('children.childInformation') }}</h3>

      <v-table class="mt-4">
        <template v-slot:default>
          <tbody>
            <tr>
              <td class="font-weight-bold">{{ $t('children.childcareType') }}</td>
              <td>{{ childType }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.loginId') }}</td>
              <td>{{ child.childCode }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.initialPassword') }}</td>
              <td>{{ child.password }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.childCode') }}</td>
              <td>
                <span v-if="child.optionalChildCode1">
                  {{ child.optionalChildCode1 }},
                </span>
                <span v-if="child.optionalChildCode2">
                  {{ child.optionalChildCode2 }}
                </span>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.dateOfBirth') }}</td>
              <td>{{ formatDisplayDate(child.dateOfBirth) }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('common.gender') }}</td>
              <td>{{ child.gender == "male" ? $t('common.male') : $t('common.female') }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.admissionDate') }}</td>
              <td>{{ formatDisplayDate(child.admissionDate) }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.graduationDate') }}</td>
              <td>{{ formatDisplayDate(child.graduationDate) }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.withdrawalDate') }}</td>
              <td>{{ formatDisplayDate(child.quitDate) }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('common.class') }}</td>
              <td>{{ classNames }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.group') }}</td>
              <td>{{ groupNames }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.extendedCarePattern') }}</td>
              <td>
                {{ child.hoursPattern ? child.hoursPattern.patternTitle : "" }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.additionalStaffForSpecialNeeds') }}</td>
              <td>{{ child.additionalStaffNum }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">{{ $t('children.maxSubsidyAmount') }}</td>
              <td>{{ child.maxSubsidyAmount }}</td>
            </tr>
            <!--
            <tr>
              <td class="font-weight-bold">登園・降園のデフォルト設定</td>
              <td>
                {{ timeIntToHourMin(child.defaultStartMin) }} -
                {{ timeIntToHourMin(child.defaultEndMin) }}
              </td>
            </tr>
            -->
          </tbody>
        </template>
      </v-table>

      <v-divider />

      <!-- auth methods -->
      <h3 class="mt-6">{{ $t('children.loginInformation') }}</h3>

      <v-table class="mt-4">
        <template v-slot:default>
          <thead>
            <tr>
              <th>{{ $t('common.name') }}</th>
              <th>{{ $t('children.relationship') }}</th>
              <th>{{ $t('children.loginMethod') }}</th>
              <th>{{ $t('children.registeredEmail') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in authMethodsResponse" :key="i">
              <td>
                {{
                  `${item.parentUser.lastName || ""} ${
                    item.parentUser.firstName || ""
                  }`
                }}
              </td>
              <td>{{ getParentTypeText(item.parentUser.parentType) }}</td>
              <td>
                {{
                  item.method == "auth0"
                    ? this.$t('children.emailAndPassword')
                    : item.method
                }}
              </td>
              <td>{{ item.method == "apple" ? "" : `${item.parentUser.email || ""}` }}</td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";

import { childAccountModule } from "@/store/dataModules/childAccountModule";
import ApiChildren from "@/api/ApiChildren";
import { parentTypeItems } from "@/constants/children";

export default defineComponent({
  async mounted() {
    const childId = this.$route.params.childId as string;
    if (childId) {
      const parsed = parseInt(childId);
      childAccountModule.setChildId(parsed);
      await childAccountModule.getChild();
    }

    await this.getAuthMethods();
  },

  data() {
    return {
      isDownloading: false,
      authMethodsResponse: [],
      parentTypeItems: parentTypeItems,
      loading: false,
    };
  },

  computed: {
    accountBreadcrumbItems(): Array<{
      title: string;
      href?: string;
      color?: string;
      disabled: boolean;
    }> {
      const title = this.childAccountBreadcrumbTitle;
      return [
        {
          title: this.$t("children.childrenParentsInfo"),
          href: "/children",
          color: "primary",
          disabled: false,
        },
        { title, disabled: true },
      ];
    },
    childAccountBreadcrumbTitle(): string {
      if (!this.child) {
        return this.$t("common.loading");
      }
      const name = `${this.child.lastName ?? ""} ${this.child.firstName ?? ""}`.trim();
      return name || this.$t("common.loading");
    },
    child() {
      return childAccountModule.child;
    },
    childType(): string {
      if (this.child) {
        if (this.child.childcareType === "normal") {
          return this.$t('children.regularChildcare');
        } else if (this.child.childcareType === "temporary") {
          return this.$t('children.temporaryChildcare');
        }
      }
      return "";
    },
    classNames(): string {
      if (this.child) {
        return this.child.nurseryClasses
          .map((nc) => `SY${nc.year} ${nc.className}`)
          .join(", ");
      }
      return "";
    },
    groupNames(): string {
      if (this.child) {
        return this.child.nurseryGroups.map((g) => g.name).join(", ");
      }
      return "";
    },
  },

  methods: {
    async invitationDownload() {
      this.isDownloading = true;
      const link = await childAccountModule.getInvitationLink();
      this.isDownloading = false;
      if (link) {
        window.open(link, "_blank");
      }
    },
    async getAuthMethods() {
      if (this.child && this.child.childId) {
        const response = await ApiChildren.getAuthMethods({
          childId: this.child.childId,
        });
        if (response) {
          this.authMethodsResponse = response.authMethods;
        }
      }
    },
    async deleteChild() {
      const alertText = this.$t('children.deleteChildWarning');
      const answer = confirm(alertText);
      if (answer) {
        if (this.child && this.child.childId) {
          this.loading = true;
          const response = await ApiChildren.deleteChild({
            childId: this.child.childId,
          }).catch(() => (this.loading = false));
          if (response) {
            alert(this.$t('children.deletedSuccessfully'));
            this.$router.push({ path: "/children" });
          }
          this.loading = false;
        }
      }
    },
    getParentTypeText(value: string): string {
      if (!value) {
        return "";
      }
      return parentTypeItems.find((p) => p.value == value)?.text || "";
    },
    formatDisplayDate(value?: string | null): string {
      return value ? dayjs(value).format("DD-MM-YYYY") : "";
    },
    timeIntToHourMin(timeInt: number) {
      const hour = Math.floor(timeInt / 60);
      const min = Math.floor(timeInt % 60);
      const minStr = min === 0 ? "00" : String(min);
      return `${hour}:${minStr}`;
    },
  },
});
</script>
